import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Star, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Container, Reveal } from "../components/primitives";
import Seo from "../components/Seo";
import api from "../lib/api";

export default function PortfolioDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setItem(null); setNotFound(false);
    api.get(`/portfolio/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="pt-40 pb-24 text-center">
        <p className="text-[#948C79]">Concept project not found.</p>
        <Button onClick={() => navigate("/portfolio")} className="mt-4 rounded-full">Back to portfolio</Button>
      </div>
    );

  if (!item) return <div className="pt-40 pb-24 text-center text-[#7E7663]">Loading…</div>;

  const accent = item.accent_color || "#C9A227";

  return (
    <div data-testid="portfolio-detail-page">
      <Seo fallback={{ title: `${item.title} — WebLaunch Studio`, meta_description: item.description, canonical: `/portfolio/${item.slug}` }} />

      {/* Hero */}
      <section className="relative pt-24">
        <div className="relative h-[70vh] min-h-[440px] overflow-hidden">
          <img src={item.hero_image} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" />
          <Container className="relative h-full flex flex-col justify-end pb-14">
            <Reveal>
              <Link to="/portfolio" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6">
                <ArrowLeft className="h-4 w-4" /> All concepts
              </Link>
              <span className="inline-block rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: accent }}>
                Concept Project · {item.industry}
              </span>
              <h1 className="mt-4 font-display text-4xl sm:text-6xl font-bold tracking-tighter text-white max-w-3xl">{item.title}</h1>
              <p className="mt-3 text-xl text-white/85 max-w-2xl">{item.tagline}</p>
            </Reveal>
          </Container>
        </div>
      </section>

      {/* Overview + stats */}
      <section className="py-20">
        <Container className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold text-[#F3EDDC]">About this concept</h2>
            <p className="mt-4 text-lg text-[#B7AF98] leading-relaxed">{item.description}</p>
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold text-[#F3EDDC]">Key features</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {(item.services || []).map((s) => (
                  <div key={s} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#141414] px-4 py-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: `${accent}1a`, color: accent }}>
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-[#D2CAB2] text-sm font-medium">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-[#0c0c0d] p-8 h-fit">
            <div className="space-y-6">
              {(item.stats || []).map((st) => (
                <div key={st.label}>
                  <div className="font-display text-3xl font-bold" style={{ color: accent }}>{st.value}</div>
                  <div className="text-sm text-[#948C79]">{st.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Gallery */}
      <section className="pb-20">
        <Container>
          <div className="grid md:grid-cols-3 gap-4">
            {(item.gallery || []).map((g, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className={`overflow-hidden rounded-2xl border border-white/10 ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
                  <img src={g} alt={`${item.title} preview ${i + 1}`} className={`w-full object-cover ${i === 0 ? "h-full min-h-[300px]" : "h-56"}`} />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Testimonial */}
      {item.testimonial && (
        <section className="pb-20">
          <Container>
            <div className="rounded-3xl p-10 sm:p-14 text-white" style={{ backgroundColor: accent }}>
              <Quote className="h-10 w-10 opacity-40" />
              <p className="mt-4 font-display text-2xl sm:text-3xl font-semibold max-w-3xl leading-snug">"{item.testimonial.quote}"</p>
              <div className="mt-6 flex items-center gap-2">
                <div className="flex">{[0,1,2,3,4].map((i) => <Star key={i} className="h-4 w-4 fill-white text-white" />)}</div>
                <span className="text-white/90 font-medium">— {item.testimonial.author}</span>
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* CTA */}
      <section className="pb-24">
        <Container>
          <div className="rounded-3xl border border-white/10 bg-[#141414] p-10 text-center shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
            <h2 className="font-display text-3xl font-bold text-[#F3EDDC]">Want a website like this?</h2>
            <p className="mt-3 text-[#B7AF98]">We build conversion-focused websites for businesses like yours.</p>
            <div className="mt-6 flex justify-center gap-3">
              <Button onClick={() => navigate("/pricing")} className="rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12 px-7">Get Your Website <ArrowRight className="ml-2 h-4 w-4" /></Button>
              <Button onClick={() => navigate("/contact")} variant="outline" className="rounded-full h-12 px-7 border-white/20">Talk to us</Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
